// Status indicator catalogue — the canonical vocabulary for every status pip,
// glyph, and meter colour used across the app.
//
// Spec: docs/logic/workspace/status-indicators.md
//
// This file is the source-of-truth for tooltip strings and the `<StatusLegend>`
// surface. Callsites reference an indicator by its stable `id`; the matching
// `label` is the hover tooltip and the `description` is the legend body.
//
// Adding a new indicator:
//   1. Add the entry to STATUS_INDICATORS with a unique kebab-case id.
//   2. Decide its visual (shape + colour token from the closed set below).
//   3. Reference it from the consuming surface via getIndicator(id) or the
//      <StatusPip indicatorId="..."> wrapper.
// The catalogue is intentionally closed — new states must land here before the
// consumer surface ships.

export type StatusIndicatorShape =
  | 'dot-filled' // 6–10px circle, solid fill
  | 'dot-hollow' // 6–10px circle, 1px border, no fill
  | 'square-filled' // 8–12px square, solid fill
  | 'square-hollow' // 12px square, hairline / dashed border, no fill
  | 'diamond' // lucide Diamond, filled
  | 'triangle' // lucide Triangle, stroke-only
  | 'pill' // text pill with coloured background
  | 'meter-band' // segment of a horizontal meter
  | 'check-pill'; // text pill + CheckCircle

export type StatusIndicatorColorToken =
  | '--state-online'
  | '--state-warning'
  | '--state-blocked'
  | '--state-info'
  | '--accent'
  | '--ink-tertiary';

export type StatusIndicatorFamily = 'state' | 'item' | 'fallback';

export type StatusIndicator = {
  /** Stable kebab-case id — referenced by callsites for tooltips + legend. */
  id: string;
  /** Family bucket — used by the StatusLegend to group entries. */
  family: StatusIndicatorFamily;
  /** Visual identity — shape + colour token together carry the meaning. */
  visual: {
    shape: StatusIndicatorShape;
    colorToken: StatusIndicatorColorToken;
    /** Optional secondary token (e.g. dashed border on a 'na' cell). */
    accent?: StatusIndicatorColorToken;
    /** Optional border style for hollow / dashed variants. */
    borderStyle?: 'solid' | 'dashed';
  };
  /** Short label — for `title` attribute and hover tooltip. */
  label: string;
  /** Longer one-sentence explanation — for the StatusLegend popover. */
  description: string;
  /** Files where this indicator currently appears. */
  consumerSurfaces: string[];
  /** Engine projection or schema field that drives this indicator's state. */
  sourceProjection: string;
};

// Human label for each family — drives StatusLegend section headers.
export const FAMILY_LABELS: Record<StatusIndicatorFamily, string> = {
  state: 'General state',
  item: 'Work items',
  fallback: 'Fallback',
};

// Canonical catalogue. Deliberately small in the foundation — extend it per
// family as real surfaces land.
export const STATUS_INDICATORS: Record<string, StatusIndicator> = {
  // ── General state (the "happy path is green" family) ─────────────────────
  'state-online': {
    id: 'state-online',
    family: 'state',
    visual: { shape: 'dot-filled', colorToken: '--state-online' },
    label: 'Ready / on track',
    description: 'Item is complete or progressing on plan.',
    consumerSurfaces: ['AIRail (assistant status)', 'OverviewScene (done items)'],
    sourceProjection: "item.state==='done'",
  },
  'state-warning': {
    id: 'state-warning',
    family: 'state',
    visual: { shape: 'triangle', colorToken: '--state-warning' },
    label: 'Needs attention',
    description:
      'Item is in-flight or showing risk — review needed but not yet blocking.',
    consumerSurfaces: [],
    sourceProjection: "item.state==='warning'",
  },
  'state-blocked': {
    id: 'state-blocked',
    family: 'state',
    visual: { shape: 'dot-filled', colorToken: '--state-blocked' },
    label: 'Blocked / overdue',
    description:
      'Item is past due or actively blocked — needs immediate attention.',
    consumerSurfaces: ['OverviewScene (blocked items)'],
    sourceProjection: "item.state==='blocked'",
  },
  'state-pending': {
    id: 'state-pending',
    family: 'state',
    visual: { shape: 'dot-hollow', colorToken: '--ink-tertiary' },
    label: 'Pending',
    description: 'Item has not started yet — no signal either way.',
    consumerSurfaces: ['OverviewScene (active items)'],
    sourceProjection: "item.state==='active'",
  },
  // ── Work items ───────────────────────────────────────────────────────────
  'item-accent': {
    id: 'item-accent',
    family: 'item',
    visual: { shape: 'pill', colorToken: '--accent' },
    label: 'Highlighted',
    description: 'Item carries the accent highlight — a count or badge.',
    consumerSurfaces: ['AdminSidebar (badge pill)'],
    sourceProjection: 'module.badge',
  },
  // ── Fallback ─────────────────────────────────────────────────────────────
  unknown: {
    id: 'unknown',
    family: 'fallback',
    visual: {
      shape: 'square-hollow',
      colorToken: '--ink-tertiary',
      borderStyle: 'dashed',
    },
    label: 'Unknown state',
    description:
      'The id passed to getIndicator() is not catalogued — add it here before shipping the surface.',
    consumerSurfaces: [],
    sourceProjection: 'n/a',
  },
};

/**
 * Resolve an indicator by id. Returns the `unknown` fallback when the id is
 * not catalogued — never throws. Callsites can lookup safely without guarding.
 */
export function getIndicator(id: string): StatusIndicator {
  return STATUS_INDICATORS[id] ?? STATUS_INDICATORS.unknown;
}

/**
 * Group indicators by family — used by StatusLegend to render section headers.
 * Preserves the insertion order of STATUS_INDICATORS so families render in the
 * spec-doc order.
 */
export function groupIndicatorsByFamily(): Record<
  StatusIndicatorFamily,
  StatusIndicator[]
> {
  const groups: Record<StatusIndicatorFamily, StatusIndicator[]> = {
    state: [],
    item: [],
    fallback: [],
  };
  for (const indicator of Object.values(STATUS_INDICATORS)) {
    groups[indicator.family].push(indicator);
  }
  return groups;
}
